import { Box, MenuItem, Skeleton, Typography } from "@mui/material";
import { FinishingTypes, PaymentTerms, Track } from "../../../types";
import MoreActionsMenu from "@/modules/common/components/ui-kit/menus/more-actions-menu";
import ListingEmptyState from "../common/empty-state";
import { useGetActiveListing } from "../../../hooks/listings.hook";
import { Trash2 } from "lucide-react";
import UnpublishListingModal from "../common/unpublish-listing/unpublish-listing-modal";
import { useMemo, useState } from "react";
import { ListingType } from "@/modules/property-tracks/dtos";
import ListingMedia from "../common/listing-media";
import PublishResaleListing from "./publish-resale-listing";
import AddResaleListing from "./forms/create";
import PropertyInfo from "../../property";
import PropertyPriceData from "../../overview-tab/price-info/property-price-data";
import EditResaleListingMenuItem from "./menu-items/edit-menu-item";
import { ResaleListingFormFields } from "@/modules/property-tracks/utils";
import Status from "@/modules/common/components/ui-kit/status/status";
import InfoCell from "@/modules/common/components/ui-kit/info-cell";
import FullPageModal from "@/modules/common/components/ui-kit/full-page-modal/full-page-modal";
import ListingStatusItem from "../common/status";


export default function ResaleActiveListingMobileTab({
  open,
  onClose,
  track,
}: Readonly<{
  open: boolean;
  onClose: () => void;
  track: Track;
}>) {
  const [unpublishListingModalOpen, setUnpublishListingModalOpen] =
    useState(false);
  const { data, isLoading } = useGetActiveListing({
    params: {
      propertyUid: track.property.uid,
      listingType: ListingType.RESALE,
    },
  });

  const values = useMemo<ResaleListingFormFields>(
    () => ({
      propertyDetails: {
        compound: track.property.compound
          ? {
              label: track.property.compound.name,
              value: track.property.compound.id,
            }
          : undefined,
        propertyType: track.property.propertyType
          ? {
              label: track.property.propertyType.name,
              value: track.property.propertyType.id,
            }
          : undefined,
        unitId: track.property.unitId ?? undefined,
        numberOfBedrooms: track.property.numberOfBedrooms ?? undefined,
        numberOfBathrooms: track.property.numberOfBathrooms ?? undefined,
        unitArea: track.property.unitArea ?? undefined,
        phase: track.property.phase ?? undefined,
        landArea: track.property.landArea ?? undefined,
        gardenArea: track.property.gardenArea ?? undefined,
        roofArea: track.property.roofArea ?? undefined,
        floorNumber: track.property.floorNumber ?? undefined,
        numberOfFloors: track.property.numberOfFloors ?? undefined,
        deliveryDate: track.property.deliveryDate
          ? new Date(track.property.deliveryDate)
          : undefined,
        lastInstallmentDate: track.property.lastInstallmentDate
          ? new Date(track.property.lastInstallmentDate)
          : undefined,
        readyToMove: track.property.readyToMove ?? undefined,
        description: track.property.description ?? undefined,
        websiteListingId: track.property.websiteListingId ?? undefined,
        finishing: track.property.finishing
          ? {
              label: FinishingTypes[track.property.finishing],
              value: track.property.finishing,
            }
          : undefined,
        amenityIds: new Set(
          track.property.propertyAmenity?.map((item) => item.amenity.id) || []
        ),
      },
      propertyPriceDetails: data?.propertyPrice
        ? {
            price: data.propertyPrice.price,
            askingPrice: data.propertyPrice.askingPrice,
            paymentType: data.propertyPrice.paymentType,
            downPayment: data.propertyPrice.downPayment,
            remainingInstallments: data.propertyPrice.remainingInstallments,
            clubhouse: data.propertyPrice.clubhouse,
            parking: data.propertyPrice.parking,
            maintenance: data.propertyPrice.maintenance,
            durationRemainingMonths: data.propertyPrice.durationRemainingMonths,
            durationRemainingYears: data.propertyPrice.durationRemainingYears,
            installmentFrequency: data.propertyPrice.installmentFrequency
              ? {
                  label: PaymentTerms[data.propertyPrice.installmentFrequency],
                  value: data.propertyPrice.installmentFrequency,
                }
              : undefined,
          }
        : undefined,
      resaleDocumentsDetails: {
        floorPlanUids:
          data?.trackDocuments
            ?.filter((d) => d.documentType === "FloorPlans")
            .map((d) => d.uid) ?? [],
        mediaUids:
          data?.trackDocuments
            ?.filter((d) => d.documentType === "Media")
            .map((d) => d.uid) ?? [],
      },
      listingDescriptionDetails: {
        descriptionAr: data?.descriptionAr ?? undefined,
        descriptionEn: data?.descriptionEn ?? undefined,
      },
    }),
    [track, data]
  );

  if (isLoading) return <Skeleton />;

  if (!data)
    return (
      <ListingEmptyState callToAction={<AddResaleListing track={track} />} />
    );
  return (
    <FullPageModal
          isOpen={open}
          title={
             <Box
                   sx={(theme) => ({
                     display: "flex",
                     gap: theme.nawy.spacing.xs,
                     flex: 1,
                     alignItems: "center",
                   })}
                 >
                   <Typography
                     sx={(theme) => theme.nawy.typography.english.variants.headline.sm}
                   >
                     Listing details
                   </Typography>
                   <ListingStatusItem status={data.status} />
                 </Box>
          }
          onClose={() => {
            onClose();
          }}
          extraButtons={
            data &&   <>
            <PublishResaleListing
              listingUid={data.uid}
              trackUid={track.uid}
              values={values}
            />
            <MoreActionsMenu>
              <EditResaleListingMenuItem
                listingUid={data.uid}
                trackUid={track.uid}
                values={values}
              />
              {data.inventoryListingId && (
                <MenuItem
                  sx={(theme) => ({
                    display: "flex",
                    gap: theme.nawy.spacing.xs,
                    color: theme.nawy.colors.semantic.text.extension.negative,
                  })}
                  onClick={() => setUnpublishListingModalOpen(true)}
                >
                  <Trash2 />
                  Remove Listing
                </MenuItem>
              )}
            </MoreActionsMenu>
          </>
          }
        >
      <Box
        sx={(t) => ({
          display: "flex",
          flexDirection: "column",
          gap: t.nawy.spacing.sm,
          backgroundColor: t.nawy.colors.semantic.layer.primary,
          padding: t.nawy.spacing.md,
          borderRadius: t.nawy.radius.xl,
        })}
      >
        <Typography
          sx={(t) => t.nawy.typography.english.variants.body.md.semibold}
        >
          Unit details
        </Typography>
        <PropertyInfo property={track.property} trackTypeName={track.trackType.name} />
      </Box>
        <Box
          sx={(t) => ({
            display: "flex",
            flexDirection: "column",
            gap: t.nawy.spacing.sm,
            backgroundColor: t.nawy.colors.semantic.layer.primary,
            padding: t.nawy.spacing.md,
            borderRadius: t.nawy.radius.xl,
            flex: 1,
          })}
        >
          <Box
            sx={(t) => ({
              display: "flex",
              gap: t.nawy.spacing.xs,
              alignItems: "center",
            })}
          >
            <Typography
              sx={(t) => t.nawy.typography.english.variants.body.md.semibold}
            >
              Payment details
            </Typography>
            {!data.propertyPrice.active && (
              <Status
                label="Out of sync"
                variant="secondary"
                color="grey"
                width="auto"
              />
            )}
          </Box>

          <PropertyPriceData
            propertyPrice={data.propertyPrice}
            extraFields={true}
          />
        </Box>
        <Box
          sx={(t) => ({
            display: "flex",
            flexDirection: "column",
            gap: t.nawy.spacing.sm,
            backgroundColor: t.nawy.colors.semantic.layer.primary,
            padding: t.nawy.spacing.md,
            borderRadius: t.nawy.radius.xl,
            flex: 1,
          })}
        >
          <Box
            sx={(t) => ({
              display: "flex",
              gap: t.nawy.spacing.xs,
              alignItems: "center",
            })}
          >
            <Typography
              sx={(t) => t.nawy.typography.english.variants.body.md.semibold}
            >
              Unit description
            </Typography>
          </Box>
          <InfoCell title="EN description" description={data.descriptionEn} />
          <InfoCell title="AR description" description={data.descriptionAr} />
        </Box>
        <ListingMedia
          title={"Listing Media"}
          documents={data.trackDocuments.filter(
            (d) => d.documentType === "Media"
          )}
        />

        <ListingMedia
          title={"Listing Floorplans"}
          documents={data.trackDocuments.filter(
            (d) => d.documentType === "FloorPlans"
          )}
        />
      <UnpublishListingModal
        listingUid={data.uid}
        open={unpublishListingModalOpen}
        setOpen={setUnpublishListingModalOpen}
      />
            </FullPageModal>

  );
}
